<script lang="ts" generics="TValues, TValue, TOption">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { cx } from '$lib/utils/cx';
    import {
        melt,
        createCombobox,
        type ComboboxOptionProps,
        createSync,
    } from '@melt-ui/svelte';
    import { onMount } from 'svelte';
    import { createAutoAbortHandler } from '$lib/utils/abort';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue | null>;
    export let loadOptions: (q: string) => Promise<TOption[]>;
    export let optionToValue: (option: TOption) => TValue;
    export let onSelect: () => void;
    export let placeholder = '';
    export let inputValueId: string | undefined = undefined;

    export let defaultLiClass = cx(
        'relative cursor-pointer scroll-my-2',
        'py-[12px] px-[20px]',
        'hover:bg-gray-200 data-[highlighted]:bg-gray-300 data-[selected]:bg-gray-200',
        'focus:outline-none ring-brand-blue ring-inset focus:ring-2 focus:rounded',
    );

    export let liClass = '';

    function toComboboxOption(option: TOption): ComboboxOptionProps<TValue> {
        return { value: optionToValue(option) };
    }

    const {
        elements: { input, option },
        states: { inputValue, selected },
    } = createCombobox<TValue>({
        forceVisible: true,
        loop: true,
        positioning: {},
    });

    $: value = form.stores().fieldValue(field);

    const sync = createSync({ selected });
    $: sync.selected($value != null ? { value: $value } : undefined, (selected) => {
        form.dispatch(form.actions().changeValue(selected?.value ?? null, field));
    });

    let options: readonly TOption[] = [];

    onMount(() => {
        const handler = createAutoAbortHandler<readonly TOption[]>();
        return inputValue.subscribe(async (q) => {
            const o = await handler(loadOptions(q));
            if (o.status === 'ok') {
                options = o.data;
            }
        });
    });

    interface $$Slots {
        option: {
            option: TOption;
            inputValue: string;
        };
    }
</script>

<div class="bg-white w-full flex items-center gap-2">
    <input
        use:melt={$input}
        class="border-0 outline-none text-gray-800 placeholder:text-gray-800 placeholder:opacity-50 text-13 w-full pl-[20px] h-[64px]"
        id={inputValueId}
        {placeholder}
    />

    <div class="pr-[20px]">
        <button
            type="button"
            aria-label="Clear"
            class="btn-reset rounded-full bg-gray-400 hover:opacity-50 size-[20px] text-white flex items-center justify-center"
        >
            <i class="tnp tnp-cross text-10" aria-hidden={true} />
        </button>
    </div>
</div>

<div role="listbox" class="flex-1 overflow-y-auto">
    <ul class="flex flex-col p-0 m-0 list-unstyled">
        {#each options as o}
            <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
            <li
                use:melt={$option(toComboboxOption(o))}
                on:m-click={() => {
                    onSelect?.();
                }}
                tabindex="0"
                class={cx(defaultLiClass, liClass)}
            >
                <slot name="option" option={o} inputValue={$inputValue} />
            </li>
        {/each}
    </ul>
</div>
